import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useRazorpay } from "react-razorpay";
import axios from "axios";
import { toast } from "sonner";
import { X, CheckCircle2, Loader2, Signal, LogOut } from "lucide-react";
import { TOPIC_OPTIONS } from "./Topics";
import { SlotPicker } from "./SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F8F6F1] border border-ink/15 focus:border-goldtext outline-none px-4 py-3 text-sm text-ink placeholder:text-neutral-400 transition-colors rounded-lg";

const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 block mb-2";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5l-3.9 3C3.2 21.3 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l3.9-3z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.3 6.6l3.9 3c.9-2.8 3.6-4.9 6.8-4.9z" />
  </svg>
);

export const BookingModal = ({ open, onClose, initialTopic = "purchase", user, onLogin, onLogout }) => {
  const { Razorpay } = useRazorpay();
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ phone: "", topic: "purchase", datetime: "" });
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("form");
      setConfirmed(null);
      setForm((f) => ({ ...f, topic: initialTopic || "purchase" }));
    }
  }, [open, initialTopic]);

  const valid = form.phone.replace(/\D/g, "").length >= 10 && form.datetime;

  const pay = async () => {
    if (!valid) {
      toast.error("WhatsApp number aur date-slot dono select karo.");
      return;
    }
    setStep("processing");
    try {
      const { data } = await axios.post(
        `${API}/bookings/create-order`,
        { phone: form.phone, topic: form.topic, preferred_datetime: form.datetime },
        { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }
      );
      const rzp = new Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "filmmakerakki",
        description: "30-min 1:1 Drone Consultation",
        order_id: data.order_id,
        prefill: { name: user.name, email: user.email, contact: form.phone },
        theme: { color: "#C9A227" },
        handler: async (resp) => {
          try {
            const verify = await axios.post(`${API}/bookings/verify`, {
              booking_id: data.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setConfirmed(verify.data.booking);
            setStep("success");
            toast.success("Booking confirmed! Email bhej di gayi hai.");
          } catch {
            setStep("form");
            toast.error("Payment verify nahi ho paya. Support se contact karein.");
          }
        },
        modal: { ondismiss: () => setStep("form") },
      });
      rzp.open();
    } catch (e) {
      setStep("form");
      if (e.response?.status === 401) {
        toast.error("Session expire ho gayi — dobara login karo.");
        onLogout();
      } else {
        toast.error(e.response?.data?.detail || "Order create nahi ho paya. Dobara try karein.");
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            data-testid="booking-modal"
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-panel text-white border border-gold/20 shadow-[0_40px_100px_rgba(0,0,0,0.7)] rounded-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-gold flex items-center gap-2">
                <Signal size={12} className="animate-blink" /> 1:1 CONSULT // ₹299 — 30 MIN
              </p>
              <button data-testid="booking-close-button" onClick={onClose} className="text-neutral-500 hover:text-gold transition-colors">
                <X size={18} />
              </button>
            </div>

            {!user && (
              <div className="px-6 py-14 text-center" data-testid="booking-login-step">
                <h3 className="font-serif text-2xl md:text-3xl text-white">
                  Login to <span className="italic text-gold-metallic">book.</span>
                </h3>
                <p className="mt-4 text-neutral-400 text-sm md:text-base leading-relaxed max-w-sm mx-auto">
                  Booking ke liye Google se login karo — aapka naam aur email wahi se aayega.
                  Baaki sirf WhatsApp number aur slot chahiye.
                </p>
                <button
                  data-testid="booking-google-login"
                  onClick={onLogin}
                  className="mt-8 w-full flex items-center justify-center gap-3 border border-white/20 rounded-full py-4 font-mono text-xs font-bold tracking-widest uppercase text-white hover:border-gold hover:text-gold transition-colors"
                >
                  <GoogleIcon /> Continue with Google
                </button>
              </div>
            )}

            {user && step === "processing" && (
              <div className="px-6 py-20 flex flex-col items-center gap-5" data-testid="booking-processing">
                <Loader2 size={32} className="animate-spin text-gold" />
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-neutral-500">
                  CONNECTING TO RAZORPAY...
                </p>
              </div>
            )}

            {user && step === "form" && (
              <div className="px-6 py-6 space-y-6">
                <div className="flex items-center gap-3 border border-white/10 px-4 py-3 rounded-full" data-testid="booking-profile-chip">
                  {user.picture && (
                    <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-8 h-8 rounded-full" />
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold truncate text-white">{user.name}</p>
                    <p className="font-mono text-[10px] text-neutral-500 truncate">{user.email}</p>
                  </div>
                  <button data-testid="booking-logout" onClick={onLogout} aria-label="Logout" className="text-neutral-500 hover:text-gold transition-colors">
                    <LogOut size={14} />
                  </button>
                </div>
                <div>
                  <label className={labelCls}>Topic</label>
                  <div className="grid grid-cols-3 gap-2">
                    {TOPIC_OPTIONS.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`booking-topic-${t.id}`}
                        onClick={() => setForm((f) => ({ ...f, topic: t.id }))}
                        className={`border px-2 py-3 font-mono text-[10px] tracking-wider uppercase transition-colors rounded-lg ${
                          form.topic === t.id
                            ? "border-gold text-gold bg-gold/10"
                            : "border-white/15 text-neutral-400 hover:border-white/40"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>WhatsApp Number *</label>
                  <input
                    data-testid="booking-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="98765 43210"
                    className={inputCls}
                  />
                  <p className="mt-1.5 font-mono text-[9px] tracking-[0.2em] text-neutral-600 uppercase">
                    Mandatory — call link isi number par aayega
                  </p>
                </div>
                <SlotPicker value={form.datetime} onChange={(v) => setForm((f) => ({ ...f, datetime: v }))} />
                <button
                  data-testid="booking-submit-pay-button"
                  onClick={pay}
                  className="w-full bg-gold text-[#070606] font-mono text-sm font-bold tracking-widest uppercase py-4 rounded-full hover:bg-goldlight transition-colors"
                >
                  Pay ₹299 &amp; Confirm
                </button>
                <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-600 uppercase text-center">
                  RAZORPAY SECURED • UPI / CARDS / NETBANKING
                </p>
              </div>
            )}

            {user && step === "success" && confirmed && (
              <div className="px-6 py-12 text-center" data-testid="booking-success">
                <CheckCircle2 size={48} className="text-gold mx-auto" />
                <h3 className="font-serif text-3xl mt-6 text-white">
                  Booking <span className="italic text-gold-metallic">confirmed.</span>
                </h3>
                <p className="mt-4 text-neutral-400 text-sm leading-relaxed">
                  {confirmed.name}, aapki 30-min call lock ho gayi hai.
                  Confirmation email bhej di gayi hai — Akki jald WhatsApp par call link share karega.
                </p>
                <div className="mt-8 border border-white/10 divide-y divide-white/10 text-left font-mono text-xs rounded-lg overflow-hidden">
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-neutral-500 tracking-widest uppercase">Topic</span>
                    <span className="text-white">{TOPIC_OPTIONS.find((t) => t.id === confirmed.topic)?.label}</span>
                  </div>
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-neutral-500 tracking-widest uppercase">Slot</span>
                    <span className="text-white">{confirmed.preferred_datetime.replace("T", " · ")}</span>
                  </div>
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-neutral-500 tracking-widest uppercase">Paid</span>
                    <span className="text-gold">₹299 ✓</span>
                  </div>
                </div>
                <button
                  data-testid="booking-done-button"
                  onClick={onClose}
                  className="mt-8 w-full border border-white/20 font-mono text-xs tracking-widest uppercase py-3.5 rounded-full text-white hover:border-gold hover:text-gold transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
